import { KeyboardEventHandler, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { useAppDispatch } from '@/shared/hooks/store';
import { CustomersController, ICustomersValidateRequest, ICustomersValidateResponse } from '@/shared/endpoints/customers';
import { Input, Button, DatePicker, Modal, Checkbox } from '@/shared/ui';
import dayjs from '@/shared/helpers/dayjs';
import useDeviceType from '@/shared/hooks/useDeviceType';
import { IErrorResponse } from '@/shared/types/interfaces';
import { dateOfBirthSchema } from '@/shared/helpers/validations';

import SetPasswordPopupFlow from './popup-flow';

import SetPasswordBg from '@/assets/images/set-password-bg.jpg';

import './style.css';
import { phoneInput } from '@/shared/helpers/phone-input';
import { PhoneAndRequired } from '@/shared/helpers/validations';
import { resetPersistState, usePersistState } from '@/shared/hooks/persistState';
import { setPasswordPersistStateKeys } from './constants';

const validationSchema = Yup.object().shape({
    phoneNumber: PhoneAndRequired(Yup.string()),
    dob: dateOfBirthSchema,
    terms: Yup.boolean()
});

const initialValues: ICustomersValidateRequest = {
    phoneNumber: '',
    dob: '',
    terms: true
};

const SetPassword = () => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [validateResult, setValidateResult] = usePersistState<ICustomersValidateResponse | null>('set-password-validate-result', null);

    const { deviceType } = useDeviceType();

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: values => {
            validateUser(values);
        }
    });

    const validateUser = async (values: ICustomersValidateRequest) => {
        const formattedDob = dayjs(values.dob).format('YYYY-MM-DD');
        const formattedPhone = values.phoneNumber.replaceAll('-', '');

        setLoading(true);

        const result = await dispatch(CustomersController.validate({ phoneNumber: formattedPhone, dob: formattedDob }));

        setLoading(false);

        if (result.meta.requestStatus === 'rejected') {
            toast.error((result.payload as IErrorResponse).message);
        } else {
            const successResponse = result.payload as ICustomersValidateResponse;
            setValidateResult(successResponse);
        }

        return;
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
        if (e.key === 'Enter') {
            formik.submitForm();
        }
    };

    const onClose = () => {
        setValidateResult(null);
        setPasswordPersistStateKeys.forEach(resetPersistState);
    }

    return (
        <div data-testid="set-password-container" className="set-password-flow flex w-full flex-col items-start lg:flex-row lg:gap-16 xl:gap-[100px]">
            <div data-testid="set-password-left-container" className="relative w-full flex-1">
                <img className="w-full login__banner" src={SetPasswordBg} alt="Medbox set-password" />
                <div className="gradient-overlay absolute inset-0 hidden lg:block"></div>
                <div className="gradient-overlay-v2 absolute inset-0 block lg:hidden"></div>
            </div>

            <div data-testid="set-password-right-container" className="relative z-10 -mt-8 w-full flex-1 pl-4 pr-4 lg:mt-24 lg:pl-0 lg:pr-8 md-max:pb-9">
                <div className="xl:max-w-[540px]">
                    <h1 data-testid="set-password-title" className="font-agrandir text-4xl text-dark lg:text-50 lg:leading-[65px] lg-max:leading-[45px] login__heading">
                        Verify your details to access your account
                    </h1>
                    <p data-testid="set-password-subtitle" className="my-3 mb-4 font-atkinsonHyperlegible text-lg lg:mb-6 lg:mt-3 lg:text-xl">
                        Confirm your date of birth and contact information to ensure secure access.
                    </p>

                    <DatePicker
                        isMobile={deviceType === 'mobile'}
                        testId='set-password-datepicker'
                        label="Birthday"
                        name="dob"
                        required
                        onChange={dob => {
                            formik.setFieldValue('dob', dob, true);
                        }}
                        value={formik.values.dob!}
                        error={formik.touched.dob && Boolean(formik.errors.dob)}
                        errorMessage={formik.errors.dob}
                        wrapperClassName="flex-1 mini:mb-[1.56rem] mini:w-full mb-4"
                    />

                    <Input
                        name="phoneNumber"
                        testId='set-password-phone-number-input'
                        className="mb-[2.25rem] lg:mb-[2.3125rem] login__password"
                        label="Phone Number"
                        value={phoneInput(formik.values.phoneNumber)}
                        required
                        placeholder="ex. 123-456-7890"
                        error={!!(formik.touched.phoneNumber && formik.errors.phoneNumber)}
                        errorMessage={formik.errors.phoneNumber}
                        onChange={event => formik.setFieldValue('phoneNumber', phoneInput(event.target.value))}
                        labelClassName="font-dmSans"
                        onKeyDown={handleKeyDown}
                    />

                    <Checkbox
                        uncheckedStrokeColor='#9FA4A1'
                        className={`mb-[25px] !items-center !gap-5 login__terms border p-5 rounded-2xl ${formik.values.terms ? 'border-primary-400' : 'border-gray-300'
                            }`}
                        label={
                            <span className="font-dmSans text-base xl:text-lg">
                                Your session may be recorded to improve user experience. Uncheck to Opt out.{' '}
                                <a
                                    target="_blank"
                                    className="cursor-pointer font-dmSansBold underline"
                                    href="https://medbox.com/cookie-policy/"
                                    style={{ fontWeight: '700' }}
                                >
                                    Cookie Policy
                                </a>
                            </span>
                        }
                        disabled={false}
                        name="terms"
                        checked={formik.values.terms}
                        onChange={formik.handleChange}
                        testId='set-password-terms-checkbox'
                    />

                    <div className="mb-36 w-full">
                        <Button
                            className='login__login'
                            sx={{
                                width: '100%',
                                fontSize: 18,
                                boxShadow: '0px 4px 7.1px 0px #0000000F'
                            }}
                            loading={loading}
                            showEndIcon
                            onClick={() => formik.handleSubmit()}
                            testId='set-password-continue-button'
                        >
                            Continue
                        </Button>
                    </div>
                </div>
            </div>

            <Modal
                contentClassName="md:!max-w-[560px] outline-none py-[40px] sm:px-[50px] sm:max-w-[540px]"
                open={!!validateResult}
                closeOnClickOutside={false}
                onClose={onClose}
            >
                {validateResult && (
                    <SetPasswordPopupFlow
                        validateValues={formik.values}
                        validateResult={validateResult}
                        onClose={onClose}
                    />
                )}
            </Modal>
        </div>
    );
};

export default SetPassword;
